/**
 * Deployment governance — observe deployment activity, assess risk, collect
 * evidence, organize approvals, and execute a controlled action ONLY where
 * that is demonstrably supported.
 *
 * Honest boundary (do not blur it): the devops layer is observe-and-govern
 * ingest for all providers; the ONE executable action is promoting a paused
 * Argo Rollout through the deployment gate + argo.promote outbox. A linked
 * deployment provider never implies actions are possible — capability
 * health (deployment.observe vs deployment.promote) is the source of truth,
 * and the readiness checks keep the two separate.
 */
import type { UseCaseDefinition } from "../types";

export const deploymentUseCase: UseCaseDefinition = {
  id: "deployment",
  version: 1,
  labelKey: "useCases.deployment.label",
  shortDescriptionKey: "useCases.deployment.short",
  fullDescriptionKey: "useCases.deployment.full",
  primaryOutcomeKey: "useCases.deployment.outcome",
  availability: "available",
  applicableWorkProfiles: ["release_devops", "manager_reviewer", "admin", "custom"],
  requiredCapabilities: ["deployment.observe", "approvals.route"],
  recommendedCapabilities: ["deployment.promote", "notifications.post"],
  suggestedBindingPurposes: ["deployment", "approvals", "status", "notifications"],
  defaultExecutionMode: "read_only",
  defaultRemediationStartMode: "manual",
  onboardingStepIds: [
    "deployment.review-recent",
    "deployment.assess-risk",
    "deployment.test-approval-route",
  ],
  dashboardSectionIds: [
    "deployment.deployments",
    "shared.approvals",
    "deployment.environments",
    "deployment.risk",
    "shared.activity",
  ],
  readinessCheckIds: [
    "deployment.binding",
    "deployment.observe-health",
    "deployment.approval-route",
    "deployment.action-capability",
  ],
  defaultGovernanceProfileId: "enterprise",
  requiredPlanFeature: "devops",
  prerequisiteDescriptionKeys: [
    "useCases.deployment.prereq.binding",
    "useCases.deployment.prereq.approvalRoute",
    "useCases.deployment.prereq.actionSupport",
  ],
};
